import z from 'zod'
import {
  FacilityIdsQueryParamSchema,
  generateRequiredPaginationQuery,
  generateRequiredPaginationResult,
} from '../../shared'
import { MigratePaymentSchema } from '../payment'
import { DiscountInterval, PromotionType } from '../pricing'
import { MigrateSubscriptionBodySchema, MigrateSubscriptionFeedingPlanHistorySchema, PartialMappedCancelReasonSchema, PartialMappedPauseReasonSchema, SearchSubscriptionsByIdsResponseSchema, SubscriptionStatus } from '../subscription'
import { ActorTypes, EventCategories } from '../user-event/types'
import { DogActivityLevel, DogBodyType, DogEatingHabits, DogGender, DogLifestage, MigrationCohort, MigrationStatus } from './types'

export const GetLegacyUserDataBodySchema = z.object({
  email: z.string().email(),
})

export type GetLegacyUserDataBody = z.infer<typeof GetLegacyUserDataBodySchema>

export const UserDataSchema = z.object({
  id: z.string().uuid(),
  legacyId: z.number().int(),
  email: z.string().email(),
  migrationStatus: z.nativeEnum(MigrationStatus),
  stripeCustomerId: z.string().optional().nullable(),
  isCustomerOnChargeApi: z.boolean(),
  userTypeId: z.number().optional(),
  puppyAlgorithmVersion: z.number().optional()
})

export type UserData = z.infer<typeof UserDataSchema>

export const AnimalStatusHistorySchema = z.object({
  id: z.string().uuid(),
  date: z.string().datetime(),
  bodyType: z.nativeEnum(DogBodyType).nullable(),
  activityLevel: z.nativeEnum(DogActivityLevel).nullable(),
  eatingHabits: z.nativeEnum(DogEatingHabits).nullable(),
  otherAllergies: z.string().nullable().optional(),
  currentWeight: z.string().nullable().optional(),
  adultWeight: z.string().nullable().optional(),
  puppyStatus: z.boolean().nullable(),
  idealWeight: z.string().nullable().optional(),
  createdAt: z.string().datetime(),
  foodAllergies: z.number().int().array().default([]),
  healthIssues: z.number().int().array().default([]),
})

export type AnimalStatusHistory = z.infer<typeof AnimalStatusHistorySchema>

export const DogDeactivationReasonSchema = z.object({
  deactivatedAt: z.string().datetime(),
  reasonLegacyId: z.number().int(),
  reasonDescription: z.string(),
  otherReason: z.string().nullable().optional(),
})

export type DogDeactivationReason = z.infer<typeof DogDeactivationReasonSchema>

export const DogUpsertBodySchema = z.object({
  id: z.string().uuid(),
  legacyId: z.number().int(),
  name: z.string().optional().nullable(),
  dateOfBirth: z.string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format. Expected YYYY-MM-DD.')
    .nullable()
    .optional(),
  dateOfBirthIsApproximate: z.boolean(),
  dateOfDeath: z.string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format. Expected YYYY-MM-DD.')
    .nullable()
    .optional(),
  dateOfDeathIsApproximate: z.boolean(),
  weight: z.string().nullable().optional(),
  gender: z.nativeEnum(DogGender).optional(),
  bodyType: z.nativeEnum(DogBodyType).optional(),
  activityLevel: z.nativeEnum(DogActivityLevel).optional(),
  eatingHabits: z.nativeEnum(DogEatingHabits).optional(),
  breeds: z.object({
    legacyId: z.number().int(),
    legacyName: z.string(),
  }).array(),
  healthIssues: z.object({
    legacyId: z.number().int(),
    legacyName: z.string(),
  }).array(),
  foodAllergies: z.object({
    legacyId: z.number().int(),
    legacyName: z.string(),
  }).array(),
  previousFoods: z.string().array(),
  previousFoodBrands: z.string().array(),
  lifestage: z.nativeEnum(DogLifestage).nullish(),
  ageAtSignupWeeks: z.number().int().nullable().optional()
})

export type DogUpsertBody = z.infer<typeof DogUpsertBodySchema>

export const DogImageSchema = z.object({
  id: z.string().uuid(),
  legacyId: z.number().int(),
  legacyFilename: z.string(),
})
export type DogImage = z.infer<typeof DogImageSchema>

export const AnimalDataSchema = z.object({
  id: z.string().uuid(),
  legacyId: z.number().int(),
  deactivationReason: DogDeactivationReasonSchema.nullable().optional(),
  images: DogImageSchema.array(),
  createdDate: z.string().datetime().nullable().optional(),
  dog: DogUpsertBodySchema,
})

export type AnimalData = z.infer<typeof AnimalDataSchema>

export const OrderDataSchema = z.object({
  id: z.string().uuid(),
})

export type OrderData = z.infer<typeof OrderDataSchema>

export const LegacyUserAddressSchema = z.object({
  // Optional because default address does not have an id in Laravel. It's not persisted in users_addresses table.
  id: z.number().int().optional(),
  userId: z.number().int(),
  shippingCity: z.string().nullable(),
  shippingPostcode: z.number().int().nullable(),
  shippingState: z.string().nullable(),
  deliveryInstructions: z.string().nullable(),
  shippingAddress1: z.string().nullable(),
  shippingAddress2: z.string().nullable(),
  shippingCompanyName: z.string().nullable(),
})

export type LegacyUserAddress = z.infer<typeof LegacyUserAddressSchema>

// order_start_index and subscription_id will need to be mapped from orchestration/subscription service
export const MappedDiscountUserSchema = z.object({
  id: z.string().uuid().nullable(),
  discountId: z.string().uuid(),
  userId: z.string().uuid(),
  discountInterval: z.nativeEnum(DiscountInterval),
  orderQuantity: z.number().int().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  deletedAt: z.string().datetime().nullable(),
})

export type MappedDiscountUser = z.infer<typeof MappedDiscountUserSchema>

export const MappedDiscountOrderSchema = z.object({
  id: z.string().uuid(),
  discountId: z.string().uuid(),
  legacyOrderId: z.number().int(),
  parentDiscountUserId: z.string().uuid().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  deletedAt: z.string().datetime().nullable(),
})

export type MappedDiscountOrder = z.infer<typeof MappedDiscountOrderSchema>

export const MultiBoxDiscountMatchSchema = z.object({
  legacyOrderId: z.number().int(),
  orderPosition: z.number().int().nonnegative(),
})

export type MultiBoxDiscountMatch = z.infer<typeof MultiBoxDiscountMatchSchema>

export const MappedMultiBoxDiscountSchema = z.object({
  discountId: z.string().uuid(),
  subscriptionId: z.string().uuid(),
  promotionUserId: z.string().uuid(),
  promotionType: z.enum([PromotionType.COUPON, PromotionType.REFERRAL_COUPON]),
  promotionValue: z.string(),
  appliedAt: z.string().datetime(),
  matches: MultiBoxDiscountMatchSchema.array().min(1),
})

export type MappedMultiBoxDiscount = z.infer<typeof MappedMultiBoxDiscountSchema>

export const MappedDiscountsSchema = z.object({
  discountUsers: MappedDiscountUserSchema.array(),
  discountOrders: MappedDiscountOrderSchema.array(),
  multiBoxDiscount: MappedMultiBoxDiscountSchema.nullable(),
})

export type MappedDiscounts = z.infer<typeof MappedDiscountsSchema>

export const GetLegacyUserDataResponseSchema = z.object({
  user: UserDataSchema,
  subscriptions: MigrateSubscriptionBodySchema.array(),
  animals: AnimalDataSchema.array(),
  orders: OrderDataSchema.array(),
  payments: MigratePaymentSchema.array(),
  addresses: LegacyUserAddressSchema.array(),
  discounts: MappedDiscountsSchema,
  latestSubscriptionStatus: z.nativeEnum(SubscriptionStatus),
  currentOrderInflight: z.boolean()
})

export type GetLegacyUserDataResponse = z.infer<typeof GetLegacyUserDataResponseSchema>

export const LegacyUserEmailSchema = z.object({
  legacyId: z.number().int(),
  email: z.string().email(),
  migrationStatus: z.nativeEnum(MigrationStatus),
  migrationLastUpdate: z.string().datetime().nullable().optional(),
})

export type LegacyUserEmail = z.infer<typeof LegacyUserEmailSchema>

export const GetLegacyUserEmailsQuerySchema = z.object({
  cohort: z.nativeEnum(MigrationCohort),
  migrationStatus: z.nativeEnum(MigrationStatus).optional(),
  orderBy: z.enum(['id', 'email']).optional(),
  orderDirection: z.enum(['ASC', 'DESC']).optional(),
}).extend(generateRequiredPaginationQuery(1000).shape)
export type GetLegacyUserEmailsQuery = z.infer<typeof GetLegacyUserEmailsQuerySchema>

export const GetLegacyUserEmailsResponseSchema = generateRequiredPaginationResult(LegacyUserEmailSchema)
export type GetLegacyUserEmailsResponse = z.infer<typeof GetLegacyUserEmailsResponseSchema>

export const GetLegacyUserEmailsByIdsBodySchema = z.object({
  ids: z.array(z.number().int()).min(1).max(500),
})
export type GetLegacyUserEmailsByIdsBody = z.infer<typeof GetLegacyUserEmailsByIdsBodySchema>

export const GetLegacyUserEmailsByIdsResponseSchema = z.object({
  data: z.array(LegacyUserEmailSchema),
})
export type GetLegacyUserEmailsByIdsResponse = z.infer<typeof GetLegacyUserEmailsByIdsResponseSchema>

export const PartialMappedUserEventSchema = z.object({
  id: z.string().uuid().describe('Event id converted to deterministic phoenix uuid'),
  userId: z.string().uuid().describe('Phoenix user id'),
  category: z.enum(EventCategories),
  idempotentKey: z.string(),
  actorId: z.string().uuid().nullable().describe('Phoenix user id - This is set to null if 0'),
  actorType: z.enum(ActorTypes),
  animalId: z.string().uuid().nullable().describe('Phoenix animal id'),
  // This needs to be mapped in orchestration
  legacyOrderId: z.number().int().nullable().describe('Laravel order id'),
  previousText: z.string().nullable(),
  currentText: z.string().nullable(),
  displayText: z.string().describe('The legacy event column'),
  performedAt: z.string().datetime(),
})

export type PartialMappedUserEvent = z.infer<typeof PartialMappedUserEventSchema>

export const GetLegacyUserEventRequestSchema = z.object({
  email: z.string().email(),
  startDate: z.string().date().optional().describe('The date to start migrating from'),
  endDate: z.string().date().optional().describe('The date to end migrating from'),
  cursor: z.coerce.number().int().nonnegative().default(0).describe('Last-returned legacy user event id; use nextCursor from the response (NOT offset math) to fetch the next page'),
  limit: z.coerce.number().int().positive().max(1000).default(100),
})

export type GetLegacyUserEventRequest = z.infer<typeof GetLegacyUserEventRequestSchema>

export const GetLegacyUserEventResponseSchema = z.object({
  data: z.array(PartialMappedUserEventSchema),
  nextCursor: z.number().int().nullable(),
})

export type GetLegacyUserEventResponse = z.infer<typeof GetLegacyUserEventResponseSchema>

export const MappedAnimalRemovalReasonSchema = z.object({
  id: z.string().uuid().describe('Deterministic record id'),
  animalId: z.string().uuid().describe('Deterministic animal id'),
  reasonId: z.number().int().describe('Laravel original reason_id - to be mapped in service'),
  otherReason: z.string().nullable(),
  deactivatedAt: z.string().datetime(),
  reactivatedAt: z.string().datetime().nullable(),
})
export type MappedAnimalRemovalReason = z.infer<typeof MappedAnimalRemovalReasonSchema>

// Legacy toggle orders — reconciliation query schemas
export const GetLegacyToggleOrdersQuerySchema = z.object({
  makeDateStart: z.string().date().describe('Start of make_date range (YYYY-MM-DD)'),
  makeDateEnd: z.string().date().describe('End of make_date range (YYYY-MM-DD)'),
  facilityIds: FacilityIdsQueryParamSchema,
  paymentStatus: z.enum(['PENDING_CHARGE', 'PAID', 'FAILED']).optional().describe('Optional Laravel payment status filter'),
})

export type GetLegacyToggleOrdersQuery = z.infer<typeof GetLegacyToggleOrdersQuerySchema>

export const LegacyToggleOrderSummarySchema = z.object({
  legacyOrderId: z.number().int().describe('Laravel orders.id'),
  boxCount: z.number().int().describe('Number of boxes for this order (from orders_boxes)'),
})

export type LegacyToggleOrderSummary = z.infer<typeof LegacyToggleOrderSummarySchema>

export const GetLegacyToggleOrdersResponseSchema = z.object({
  data: LegacyToggleOrderSummarySchema.array().describe('Order ID + box count pairs'),
  meta: z.object({
    total: z.number().int().describe('Total number of orders'),
    totalBoxes: z.number().int().describe('Total number of boxes across all orders (from orders_boxes)'),
  }),
})

export type GetLegacyToggleOrdersResponse = z.infer<typeof GetLegacyToggleOrdersResponseSchema>

export const GetLegacyHistoricalDataRequestSchema = z.object({
  email: z.string().email(),
})

export type GetLegacyHistoricalDataRequest = z.infer<typeof GetLegacyHistoricalDataRequestSchema>

export const AnimalStatusHistoryResponseSchema = z.object({
  dogId: z.string().uuid(),
  statusHistory: AnimalStatusHistorySchema.array().default([]),
})

export type AnimalStatusHistoryResponse = z.infer<typeof AnimalStatusHistoryResponseSchema>

export const GetLegacyAnimalHistoricalDataResponseSchema = z.object({
  animalRemovals: z.array(MappedAnimalRemovalReasonSchema),
  animalStatusHistory: AnimalStatusHistoryResponseSchema.array(),
})

export const GetLegacySubscriptionHistoricalDataResponseSchema = z.object({
  feedingPlanHistory: MigrateSubscriptionFeedingPlanHistorySchema.array(),
  cancelReasons: z.array(PartialMappedCancelReasonSchema),
  pauseReasons: z.array(PartialMappedPauseReasonSchema).default([]),
})

export type GetLegacyAnimalHistoricalDataResponse = z.infer<typeof GetLegacyAnimalHistoricalDataResponseSchema>

export type GetLegacySubscriptionHistoricalDataResponse = z.infer<typeof GetLegacySubscriptionHistoricalDataResponseSchema>

export const GetLegacyHistoricalDataResponseSchema = z.object({
  userId: z.string().uuid(),
  animal: GetLegacyAnimalHistoricalDataResponseSchema,
  subscription: GetLegacySubscriptionHistoricalDataResponseSchema,
})

export type GetLegacyHistoricalDataResponse = z.infer<typeof GetLegacyHistoricalDataResponseSchema>

// ─── Refer Coupon Migration ─────────────────────────────────────────────────
// Maps to Laravel `refer_coupons` table. The exporter filters out rows with
// null coupon_code or null coupon_value_percentage (and non-percentage /
// non-all discount variants) so consumers can assume `couponCode` and
// `couponValuePercentage` are set for all returned rows.
export const ReferCouponSchema = z.object({
  id: z.number().int(),
  couponOwnerId: z.number().int(),
  /**
   * Phoenix UUID for the coupon owner, resolved by the exporter via
   * `laravelUserIdToPhoenixId`. Deterministic — either the hardcoded
   * non-deterministic mapping or `uuidFromInteger(couponOwnerId)`.
   * Consumers use this directly as `promotions.referrer_user_id`.
   */
  referrerUserId: z.string().uuid(),
  couponCode: z.string().nullable(),
  discountAmountType: z.enum(['percentage', 'dollar']),
  discountType: z.enum(['all', 'meals', 'treats', 'supplements']),
  dateCreated: z.string().datetime().nullable(),
  active: z.boolean(),
  couponValuePercentage: z.number().int().nullable(),
})

export type ReferCoupon = z.infer<typeof ReferCouponSchema>

export const GetReferCouponCountResponseSchema = z.object({
  total: z.number().int(),
  uniquePercentages: z.array(z.number()),
})

export type GetReferCouponCountResponse = z.infer<typeof GetReferCouponCountResponseSchema>

export const GetReferCouponPageQuerySchema = z.object({
  cursor: z.coerce.number().int().default(0),
  limit: z.coerce.number().int().default(500),
})

export type GetReferCouponPageQuery = z.infer<typeof GetReferCouponPageQuerySchema>

export const GetReferCouponPageResponseSchema = z.object({
  data: z.array(ReferCouponSchema),
  nextCursor: z.number().int().nullable(),
})

export type GetReferCouponPageResponse = z.infer<typeof GetReferCouponPageResponseSchema>

export const GetReferCouponsByIdsBodySchema = z.object({
  ids: z.array(z.number().int()).min(1).max(500),
})

export type GetReferCouponsByIdsBody = z.infer<typeof GetReferCouponsByIdsBodySchema>

export const GetReferCouponsByIdsResponseSchema = z.object({
  data: z.array(ReferCouponSchema),
})

export type GetReferCouponsByIdsResponse = z.infer<typeof GetReferCouponsByIdsResponseSchema>

// ─── Refer Coupon User Migration ──────────────────────────────────────────────
// Maps to Laravel `refer_coupons_users` table.
export const ReferCouponUserSchema = z.object({
  id: z.number().int(),
  rafCouponId: z.number().int(),
  couponUserId: z.number().int(),
  referCampaign: z.string().nullable(),
  dateCreated: z.string().datetime().nullable(),
  seenByRafCoupon: z.boolean(),
  discountPercentage: z.number().int(),
})

export type ReferCouponUser = z.infer<typeof ReferCouponUserSchema>

export const GetReferCouponUserCountResponseSchema = z.object({
  total: z.number().int(),
})

export type GetReferCouponUserCountResponse = z.infer<typeof GetReferCouponUserCountResponseSchema>

export const GetReferCouponUserPageQuerySchema = z.object({
  cursor: z.coerce.number().int().default(0),
  limit: z.coerce.number().int().default(500),
})

export type GetReferCouponUserPageQuery = z.infer<typeof GetReferCouponUserPageQuerySchema>

export const GetReferCouponUserPageResponseSchema = z.object({
  data: z.array(ReferCouponUserSchema),
  nextCursor: z.number().int().nullable(),
})

export type GetReferCouponUserPageResponse = z.infer<typeof GetReferCouponUserPageResponseSchema>

export const SearchLegacySubscriptionsByIdsBodySchema = z.object({
  ids: z.array(z.number().int()).min(1).max(500),
})

export type SearchLegacySubscriptionsByIdsBody = z.infer<typeof SearchLegacySubscriptionsByIdsBodySchema>

export const SearchLegacySubscriptionsByIdsResponseSchema = SearchSubscriptionsByIdsResponseSchema

export type SearchLegacySubscriptionsByIdsResponse = z.infer<typeof SearchLegacySubscriptionsByIdsResponseSchema>

export const SignupMealPlan = z.enum(['full', 'half', 'starter', 'trial', 'trial-7x200g', 'trial-14x200g', 'starter-half', 'starter-1-week', 'starter-half-1-week', 'starter-quarter-2-week', 'starter-full-1w-over-2w'])
export type SignupMealPlan = z.infer<typeof SignupMealPlan>
